import { GQLDesignPageFragment } from '@theprintbar/graphql';
import { BrandColours, TeamNameOption, mapTeamNameOptionsToList, withDomain } from '@theprintbar/shared';
import type { CreativeEditorInstance } from '../../../CreativeEditor';
import { hexToRGBAColor } from '../../colours';
import { ExportLabel, setExportLabel } from '../../exportMetadata';
import { DockOrderComponent } from '../setupDock';

export function getCurrentTeamNameVariables(editor: CreativeEditorInstance) {
	if (!editor) return [];
	const variables = editor.engine.variable
		.findAll()
		.filter(v => Object.values(TeamNameOption).includes(v as TeamNameOption)) as TeamNameOption[];
	const pages = editor.engine.scene.getPages();
	const blocks = pages.reduce((acc, page) => {
		return [...acc, ...editor.engine.block.getChildren(page)];
	}, [] as number[]);

	return variables.filter(v => {
		const hasBlocks = !!blocks.find(block => {
			if (editor.engine.block.getType(block) !== '//ly.img.ubq/text') return false;
			const text = editor.engine.block.getString(block, 'text/text');
			return text.includes(`{{${v}}}`);
		});

		if (!hasBlocks) {
			editor.engine.variable.remove(v);
		}
		return hasBlocks;
	});
}

export async function resetTeamNameAssets(editor: CreativeEditorInstance, content?: GQLDesignPageFragment) {
	if (!editor) return [];
	const teamNameOptions = mapTeamNameOptionsToList(content?.teamNameOptions?.structure ?? []);
	const pages = editor.engine.scene.getPages();
	const blocks = pages.reduce((acc, page) => {
		return [...acc, ...editor.engine.block.getChildren(page)];
	}, [] as number[]);

	blocks.forEach(block => {
		if (editor.engine.block.getType(block) !== '//ly.img.ubq/text') return false;
		const text = editor.engine.block.getString(block, 'text/text');
		//Extract a variable name from between {{}} if it exists
		const teamName = teamNameOptions?.find(({ id }) => id === text.match(/{{(.*?)}}/)?.[1]);
		if (!teamName) return;
		editor.engine.variable.setString(teamName.id, teamName.initialValue);
	});
}

export function setTeamNameAssets(editor: CreativeEditorInstance, teamNames: Array<{ id: string; name: string }>) {
	if (!editor) return;
	let hasUpdated = false;
	teamNames.forEach(({ id, name }) => {
		const original = editor.engine.variable.getString(id);
		if (original !== name) {
			editor.engine.variable.setString(id, name);
			hasUpdated = true;
		}
	});
	return hasUpdated;
}

export function addTeamnameDock(editor: CreativeEditorInstance, WEB_DOMAIN: string, content?: GQLDesignPageFragment): DockOrderComponent {
	if (!editor) throw 'Editor not found';
	const id = 'teamNames';
	if (!content?.teamNameOptions?.structure) throw 'No team name options found';
	const teamNameOptions = mapTeamNameOptionsToList(content?.teamNameOptions?.structure);

	editor?.ui.addAssetLibraryEntry({
		id,
		gridColumns: 2,
		gridItemHeight: 'square',
		cardLabel: asset => teamNameOptions?.find(option => option && option.id === asset.id)?.label,
		cardLabelPosition: () => 'below',
		cardLabelStyle: asset => ({ fontSize: '14px' }),
		sourceIds: [id],
	});

	editor?.engine.asset.addSource({
		id,
		findAssets: async () => {
			const options = teamNameOptions?.filter(option => option && typeof option.label === 'string') ?? [];
			return {
				assets: options.map(option => ({
					id: option?.id,
					label: option?.label ?? undefined,
					meta: {
						thumbUri: option.preview?.image.url,
					},
				})),
				currentPage: 1,
				total: options.length,
			};
		},
		applyAsset: async asset => {
			const teamName = teamNameOptions?.find(({ id }) => id === asset.id);
			if (!teamName) return;

			const { block, scene } = editor.engine;
			const text = await editor.engine.asset.defaultApplyAsset({
				id: 'text',
				meta: {
					blockType: '//ly.img.ubq/text',
				},
			});
			if (!text) return;
			setExportLabel(editor, text, ExportLabel.TeamName);

			block.replaceText(text, `{{${asset.id}}}`);
			block.setTextFontSize(text, teamName.cardStyles.fontSize || 40);
			block.setFloat(text, 'text/fontSize', teamName.defaultSize || 40);
			block.setWidthMode(text, 'Auto');
			block.setHeightMode(text, 'Auto');
			block.setTextColor(text, hexToRGBAColor(BrandColours.Orange));

			block.setPlaceholderEnabled(text, true);
			block.setPlaceholderBehaviorEnabled(text, true);
			editor.engine.variable.setString(asset.id, teamName.initialValue as string);

			setTimeout(() => {
				block.setEnum(text, 'text/horizontalAlignment', 'Center');

				const page = scene.getCurrentPage() ?? 1;
				const scaleFactor = parseFloat(block.getMetadata(page, 'scaleFactor') || '1');
				const pageHeight = block.getHeight(text);

				const pageWidth = block.getWidth(text);
				const defaultHeight = (teamName.defaultSize ?? 40) * scaleFactor;
				const maxHeight = pageHeight * ((teamName.maxHeightPercentage ?? 100) / 100);
				const finalHeight = Math.min(defaultHeight, maxHeight);

				const yPercent = teamName.position?.y ?? 50;
				const xPercent = teamName.position?.x ?? 50;

				const yPos = (yPercent / 100) * finalHeight;
				const xPos = (xPercent / 100) * pageWidth;

				block.setPositionX(text, xPos);
				block.setPositionY(text, yPos);
			}, 0); // delay so we can get the height of the text

			return text;
		},
	});

	return {
		id: 'ly.img.assetLibrary.dock',
		key: id,
		icon: '@imgly/CustomLibrary',
		label: 'libraries.teamNames.label',
		entries: [id],
	};
}
